'use client'

import { Avatar, AvatarFallback } from '@/components/ui/avatar.tsx'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth'
import { getArenaUrl, getResearchHomeUrl } from '@/lib/arena'
import {
  type DashboardBadgeCounts,
  DASHBOARD_TAB_LABELS,
  dashboardBadgeTotal,
  dashboardTabBadgeCount,
  dashboardTabsForHost,
  formatBadgeCount,
} from '@/lib/dashboard-tabs'
import { languages, useI18n } from '@/lib/i18n'
import { useGameAttention } from '@/lib/game-attention'
import { isLobbyEnabled } from '@/lib/lobby'
import { publicLanguageSwitchUrl } from '@/lib/public-language-switch'
import { useTRPC } from '@/lib/trpc'
import {
  getPathLanguage,
  withLanguagePath,
} from '@/lib/url-language-utils'
import { cn, getInitials } from '@/lib/utils.ts'
import { useSocialOverview } from '@/social/social-provider'
import type { SocialOverview } from '@/social/types'
import { useQuery } from '@tanstack/react-query'
import { ArrowUpRight, ChartColumn, Menu, Moon, Sun, X } from 'lucide-react'
import { useTheme } from 'next-themes'
import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { platform } from '@/lib/platform'

const ARENA_NAV_ITEMS = [
  { id: '/tournaments', key: 'tournament.entry' },
  { id: '/lobby', key: 'nav.lobby' },
  { id: '/new', key: 'nav.createGame' },
  { id: '/review', key: 'nav.review' },
  { id: '/play/rule', key: 'nav.steamRules' },
  { id: '/play/roles', key: 'nav.steamRoles' },
  { id: '/games', key: 'nav.gameArchive' },
  { id: '/forum', key: 'nav.forum' },
] as const

const ICON_ONLY_BENCHMARK_LANGUAGES = new Set(['ja', 'es', 'fr'])

function arenaNavItems(): ReadonlyArray<{ id: string, key: string }> {
  if (isLobbyEnabled())
    return ARENA_NAV_ITEMS
  return ARENA_NAV_ITEMS.filter(item => item.id !== '/lobby')
}

function isArenaItemActive(itemId: string, pathname: string): boolean {
  if (itemId === '/tournaments')
    return pathname === '/tournaments' || pathname.startsWith('/tournaments/')
  if (itemId === '/forum')
    return pathname === '/forum' || pathname.startsWith('/forum/')
  if (itemId === '/review')
    return pathname === '/review' || pathname.startsWith('/review/')
  return pathname === itemId
}

function getArenaItemPath(
  itemId: string,
  language: string,
  pathHasLanguage: boolean,
): string {
  if (itemId === '/forum' && platform.type !== 'steam')
    return withLanguagePath(itemId, language)

  const path = pathHasLanguage
    ? itemId
    : withLanguagePath(itemId, language)
  return path
}

export function Navigation({
  minimal = false,
  notificationSetupEnabled = false,
}: {
  minimal?: boolean
  notificationSetupEnabled?: boolean
} = {}) {
  const [activeSection, setActiveSection] = useState('hero')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { theme, setTheme } = useTheme()
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false)
  const { language, t } = useI18n()
  const { isAuthenticated, user, authResolved } = useAuth()
  const gameAttention = useGameAttention()
  const trpc = useTRPC()
  const navigate = useNavigate()
  const location = useLocation()
  const pathHasLanguage = platform.type === 'steam'
    || (typeof window !== 'undefined'
      && getPathLanguage(window.location.pathname) !== null)
  const arenaOrigin = getArenaUrl()
  const gameHref = arenaOrigin
    ? `${arenaOrigin}${withLanguagePath('/', language)}`
    : `${withLanguagePath('/', language)}?arena`
  const benchmarkHref = minimal && platform.type === 'web'
    ? getResearchHomeUrl(language)
    : null
  const benchmarkTextFits = !ICON_ONLY_BENCHMARK_LANGUAGES.has(language)
  const brandClassName = cn(
    'me-auto flex flex-shrink-0 items-center gap-3 transition-opacity',
    'hover:opacity-80 md:me-0',
    minimal && 'min-h-11 min-w-11 justify-center rounded-md',
    minimal && 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    minimal && 'focus-visible:ring-offset-2 md:min-h-0 md:min-w-0 md:justify-start',
  )
  const brandContent = (
    <>
      {minimal && (
        <img
          src="/icons/mentiss-512.png"
          alt=""
          className="size-8 rounded-full object-cover md:hidden"
        />
      )}
      <img
        src="/asset/logo.jpg"
        alt="Mentiss Logo"
        className="hidden size-8 rounded-full object-cover md:inline"
      />
      <span className={cn('text-xl font-bold text-foreground', minimal && 'hidden md:inline')}>
        Mentiss
      </span>
    </>
  )
  const dashboardTabs = dashboardTabsForHost(notificationSetupEnabled)

  const { data: unreadData } = useQuery({
    ...trpc.forumRouter.unreadNotificationCount.queryOptions(),
    enabled: isAuthenticated,
    refetchInterval: 60_000,
  })
  const socialOverview = useSocialOverview(isAuthenticated).data as
    | SocialOverview
    | undefined
  const badgeCounts: DashboardBadgeCounts = {
    inbox: authResolved && user ? unreadData?.unreadCount ?? 0 : 0,
    social: authResolved && user
      ? (socialOverview?.counts.unread ?? 0)
      + (socialOverview?.counts.incoming ?? 0)
      : 0,
    games: gameAttention.awaitingCount,
  }
  const notificationCount = dashboardBadgeTotal(badgeCounts)
  const notificationLabel = formatBadgeCount(notificationCount)
  const profileLabel = [
    t('nav.steamDashboard'),
    badgeCounts.inbox > 0
      ? `${t(DASHBOARD_TAB_LABELS.inbox)}: ${badgeCounts.inbox}`
      : null,
    badgeCounts.social > 0
      ? `${t(DASHBOARD_TAB_LABELS.social)}: ${badgeCounts.social}`
      : null,
    gameAttention.awaitingCount > 0
      ? `${t('gameAttention.awaitingInput')}: ${badgeCounts.games}`
      : null,
  ].filter(Boolean).join(', ')
  const lobbyBadge = gameAttention.awaitingCount > 0 && (
    <span
      className="ms-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground"
      data-testid="navigation-lobby-badge"
      aria-label={`${t('gameAttention.awaitingInput')}: ${gameAttention.awaitingCount}`}
    >
      {formatBadgeCount(gameAttention.awaitingCount)}
    </span>
  )

  const goToDashboardTab = (tab?: string) => {
    const suffix = tab ? `?tab=${tab}` : ''
    if (pathHasLanguage)
      navigate(`/dashboard${suffix}`)
    else window.location.assign(`${withLanguagePath('/dashboard', language)}${suffix}`)
    setMobileMenuOpen(false)
  }

  const goToSignIn = () => {
    if (pathHasLanguage)
      navigate('/signin')
    else window.location.assign(withLanguagePath('/signin', language))
    setMobileMenuOpen(false)
  }

  useEffect(() => {
    const handleScroll = () => {
      const sectionItems = platform.navItems.filter(
        item => item.type === 'section',
      )
      const sections = sectionItems.map(item =>
        document.getElementById(item.id),
      )
      const scrollPosition = window.scrollY + 100

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i]
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sectionItems[i]!.id)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (location.pathname !== '/' || !location.hash)
      return

    const hash = location.hash.slice(1)
    if (!platform.navItems.some(item => item.type === 'section' && item.id === hash))
      return

    const frame = window.requestAnimationFrame(() => {
      const element = document.getElementById(hash)
      if (element) {
        setActiveSection(hash)
        element.scrollIntoView({ behavior: 'smooth' })
      }
    })
    return () => window.cancelAnimationFrame(frame)
  }, [location.pathname, location.hash])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement

      if (showLanguageDropdown && !target.closest('[data-language-dropdown]')) {
        setShowLanguageDropdown(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [showLanguageDropdown])

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      if (pathHasLanguage)
        navigate(`/#${sectionId}`)
      else window.location.assign(`${withLanguagePath('/', language)}#${sectionId}`)
      setMobileMenuOpen(false)
      return
    }

    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
      setMobileMenuOpen(false)

      const url = new URL(window.location.href)
      url.hash = `#${sectionId}`
      window.history.replaceState({}, '', url.toString())
    }
  }

  const currentLang
    = languages.find(lang => lang.code === language) || languages[0]!

  return (
    <nav className="fixed left-0 top-0 z-50 w-dvw border-b border-border bg-background/95 backdrop-blur-md">
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <Link
          to={pathHasLanguage ? '/' : withLanguagePath('/', language)}
          reloadDocument={!pathHasLanguage}
          onClick={(event) => {
            if (!minimal && location.pathname === '/') {
              event.preventDefault()
              scrollToSection('hero')
            }
          }}
          className={brandClassName}
          aria-label="Go to home"
        >
          {brandContent}
        </Link>

        { }
        {minimal && (
          <div className="hidden flex-1 items-center justify-center gap-2 md:flex lg:gap-4">
            {arenaNavItems().map((item) => {
              const isActive = isArenaItemActive(item.id, location.pathname)
              const targetPath = getArenaItemPath(item.id, language, pathHasLanguage)
              const className = `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
              }`
              if (item.id === '/forum') {
                return (
                  <a key={item.id} href={targetPath} className={className}>
                    {t(item.key)}
                  </a>
                )
              }
              return (
                <Link
                  key={item.id}
                  to={targetPath}
                  reloadDocument={!pathHasLanguage}
                  className={className}
                >
                  {t(item.key)}
                  {item.id === '/lobby' && lobbyBadge}
                </Link>
              )
            })}
          </div>
        )}

        { }
        {!minimal && (
          <div className="hidden flex-1 items-center justify-center space-x-1 md:flex lg:space-x-4">
            {platform.navItems.map((item) => {
              const isRouteActive
                = item.id !== '/' && (item.type === 'route'
                  ? item.id.includes('?')
                    ? location.pathname + location.search === item.id
                    : location.pathname === item.id
                  : activeSection === item.id)
              const linkClass = `rounded-md px-2 py-2 text-sm font-medium transition-colors hover:text-foreground lg:px-3 ${
                isRouteActive
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted/50'
              }`
              if (item.id === '/') {
                return (
                  <a key={item.id} href={gameHref} className={linkClass}>
                    {t(item.key)}
                  </a>
                )
              }
              return (
                <Link
                  key={item.id}
                  to={item.type === 'section'
                    ? `${pathHasLanguage ? '/' : withLanguagePath('/', language)}#${item.id}`
                    : pathHasLanguage ? item.id : withLanguagePath(item.id, language)}
                  reloadDocument={!pathHasLanguage}
                  onClick={(event) => {
                    if (item.type === 'section') {
                      event.preventDefault()
                      scrollToSection(item.id)
                    }
                  }}
                  className={linkClass}
                >
                  {t(item.key)}
                  {item.id === '/lobby' && lobbyBadge}
                </Link>
              )
            })}
          </div>
        )}

        <div className="hidden flex-shrink-0 items-center gap-2 md:flex">
          {benchmarkHref && (
            <>
              <a
                href={benchmarkHref}
                className={cn(
                  'hidden items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium',
                  'text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground',
                  benchmarkTextFits && 'xl:inline-flex',
                )}
                data-testid="navigation-benchmark-link"
              >
                {t('nav.benchmark')}
                <ArrowUpRight className="size-3.5 rtl:-scale-x-100" aria-hidden="true" />
              </a>
              <Button asChild variant="ghost" size="icon" className={cn(benchmarkTextFits && 'xl:hidden')}>
                <a
                  href={benchmarkHref}
                  title={t('nav.benchmark')}
                  aria-label={t('nav.benchmark')}
                  data-testid="navigation-benchmark-icon-link"
                >
                  <ChartColumn className="size-5" aria-hidden="true" />
                </a>
              </Button>
            </>
          )}
          { }
          <div data-language-dropdown className="relative flex">
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              title={`Current language: ${currentLang.name}`}
              aria-expanded={showLanguageDropdown}
              aria-controls="desktop-language-options"
              onClick={() => {
                setShowLanguageDropdown(!showLanguageDropdown)
              }}
            >
              <span className="text-lg leading-none">{currentLang.flag}</span>
              <span className="sr-only">
                Select language - Current: {currentLang.name}
              </span>
            </Button>

            <div id="desktop-language-options" className={`absolute end-0 top-full z-50 mt-2 w-40 rounded-md border border-border bg-popover shadow-md ${showLanguageDropdown ? '' : 'hidden'}`}>
              {languages.map(option => (
                <a
                  key={option.code}
                  href={publicLanguageSwitchUrl(option.code)}
                  onClick={() => setShowLanguageDropdown(false)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-muted ${
                    currentLang.code === option.code ? 'bg-muted' : ''
                  }`}
                >
                  <span className="text-base">{option.flag}</span>
                  <span>{option.name}</span>
                </a>
              ))}
            </div>
          </div>

          { }
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          >
            <Sun className="size-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute size-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            <span className="sr-only">Toggle theme</span>
          </Button>

          {isAuthenticated ? (
            <Button
              variant="ghost"
              className="relative size-8 rounded-full p-0"
              onClick={() => goToDashboardTab()}
              aria-label={profileLabel}
            >
              <Avatar className="size-8">
                <AvatarFallback className="text-sm">
                  {getInitials(user?.email)}
                </AvatarFallback>
              </Avatar>
              {notificationCount > 0 && (
                <span data-testid="navigation-profile-badge" aria-hidden="true" className="absolute -end-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground">
                  {notificationLabel}
                </span>
              )}
            </Button>
          ) : (
            platform.showSignIn && (
              <Button
                variant="ghost"
                onClick={goToSignIn}
                className="px-3 py-2 text-sm font-medium"
              >
                {t('nav.signIn')}
              </Button>
            )
          )}
        </div>

        <div className="flex flex-shrink-0 items-center gap-1 md:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          >
            <Sun className="size-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute size-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            <span className="sr-only">Toggle theme</span>
          </Button>

          <div data-language-dropdown className="relative flex">
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              title={`Current language: ${currentLang.name}`}
              aria-expanded={showLanguageDropdown}
              aria-controls="mobile-language-options"
              onClick={() => {
                setShowLanguageDropdown(!showLanguageDropdown)
              }}
            >
              <span className="text-lg leading-none">{currentLang.flag}</span>
              <span className="sr-only">
                Select language - Current: {currentLang.name}
              </span>
            </Button>

            <div id="mobile-language-options" className={`absolute end-0 top-full z-50 mt-2 w-40 rounded-md border border-border bg-popover shadow-md ${showLanguageDropdown ? '' : 'hidden'}`}>
              {languages.map(option => (
                <a
                  key={option.code}
                  href={publicLanguageSwitchUrl(option.code)}
                  onClick={() => setShowLanguageDropdown(false)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-muted ${
                    currentLang.code === option.code ? 'bg-muted' : ''
                  }`}
                >
                  <span className="text-base">{option.flag}</span>
                  <span>{option.name}</span>
                </a>
              ))}
            </div>
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="relative"
            aria-label={notificationCount > 0 ? profileLabel : undefined}
            aria-controls="mobile-navigation-menu"
            aria-expanded={mobileMenuOpen}
            onClick={() => {
              setShowLanguageDropdown(false)
              setMobileMenuOpen(!mobileMenuOpen)
            }}
          >
            {mobileMenuOpen ? (
              <X className="size-4" />
            ) : (
              <Menu className="size-4" />
            )}
            {
            }
            {!mobileMenuOpen && isAuthenticated && notificationCount > 0 && (
              <span data-testid="navigation-profile-badge" aria-hidden="true" className="absolute -end-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground">
                {notificationLabel}
              </span>
            )}
            <span className="sr-only">Toggle menu</span>
          </Button>
        </div>
      </div>

      { }
      {mobileMenuOpen && (
        <div
          id="mobile-navigation-menu"
          className="border-t border-border bg-background/95 backdrop-blur-md md:hidden"
        >
          <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-h-[calc(100dvh-4rem)] space-y-3 overflow-y-auto px-2 pb-4 pt-3">
              {(isAuthenticated || platform.showSignIn) && (
                <div className="space-y-1 border-b border-border pb-3">
                  {isAuthenticated ? (
                    <>
                      {
                      }
                      <div className="flex w-full items-center gap-3 px-3 py-2">
                        <Avatar className="size-8">
                          <AvatarFallback className="text-sm">
                            {getInitials(user?.email)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-muted-foreground">
                          {user?.email || t('nav.steamDashboard')}
                        </span>
                      </div>
                      {dashboardTabs.map((tab) => {
                        const isActive
                          = location.pathname === '/dashboard'
                            && (location.search.includes(`tab=${tab}`)
                              || (tab === 'subscription' && !location.search.includes('tab=')))
                        return (
                          <button
                            key={tab}
                            type="button"
                            onClick={() => goToDashboardTab(tab)}
                            className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-start text-sm font-medium transition-colors ${
                              isActive
                                ? 'bg-muted text-foreground'
                                : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                            }`}
                          >
                            <span>{t(DASHBOARD_TAB_LABELS[tab])}</span>
                            {dashboardTabBadgeCount(tab, badgeCounts) > 0 && (
                              <span
                                className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground"
                                data-testid={tab === 'games' ? 'dashboard-games-badge' : undefined}
                                aria-label={tab === 'games' ? `${t('gameAttention.awaitingInput')}: ${badgeCounts.games}` : undefined}
                              >
                                {formatBadgeCount(
                                  dashboardTabBadgeCount(tab, badgeCounts),
                                )}
                              </span>
                            )}
                          </button>
                        )
                      })}
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={goToSignIn}
                      className="block w-full rounded-md px-3 py-2 text-start text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
                    >
                      {t('nav.signIn')}
                    </button>
                  )}
                </div>
              )}

              <div className="space-y-1">
                {minimal
                  ? arenaNavItems().map((item) => {
                      const isActive = isArenaItemActive(item.id, location.pathname)
                      const targetPath = getArenaItemPath(item.id, language, pathHasLanguage)
                      const className = `block w-full rounded-md px-3 py-2 text-start text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-muted text-foreground'
                          : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                      }`
                      if (item.id === '/forum') {
                        return (
                          <a
                            key={item.id}
                            href={targetPath}
                            onClick={() => setMobileMenuOpen(false)}
                            className={className}
                          >
                            {t(item.key)}
                          </a>
                        )
                      }
                      return (
                        <Link
                          key={item.id}
                          to={targetPath}
                          reloadDocument={!pathHasLanguage}
                          onClick={() => setMobileMenuOpen(false)}
                          className={className}
                        >
                          {t(item.key)}
                          {item.id === '/lobby' && lobbyBadge}
                        </Link>
                      )
                    })
                  : platform.navItems.map((item) => {
                      const isRouteActive
                        = item.id !== '/' && (item.type === 'route'
                          ? item.id.includes('?')
                            ? location.pathname + location.search === item.id
                            : location.pathname === item.id
                          : activeSection === item.id)
                      const linkClass = `block w-full rounded-md px-3 py-2 text-start text-sm font-medium transition-colors ${
                        isRouteActive
                          ? 'bg-muted text-foreground'
                          : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                      }`
                      if (item.id === '/') {
                        return (
                          <a
                            key={item.id}
                            href={gameHref}
                            onClick={() => setMobileMenuOpen(false)}
                            className={linkClass}
                          >
                            {t(item.key)}
                          </a>
                        )
                      }
                      return (
                        <Link
                          key={item.id}
                          to={item.type === 'section'
                            ? `${pathHasLanguage ? '/' : withLanguagePath('/', language)}#${item.id}`
                            : pathHasLanguage ? item.id : withLanguagePath(item.id, language)}
                          reloadDocument={!pathHasLanguage}
                          onClick={(event) => {
                            if (item.type === 'section') {
                              event.preventDefault()
                              scrollToSection(item.id)
                            }
                            else {
                              setMobileMenuOpen(false)
                            }
                          }}
                          className={linkClass}
                        >
                          {t(item.key)}
                          {item.id === '/lobby' && lobbyBadge}
                        </Link>
                      )
                    })}
                {benchmarkHref && (
                  <a
                    href={benchmarkHref}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex w-full items-center gap-1 rounded-md px-3 py-2 text-start text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
                    data-testid="mobile-navigation-benchmark-link"
                  >
                    {t('nav.benchmark')}
                    <ArrowUpRight className="size-3.5 rtl:-scale-x-100" aria-hidden="true" />
                  </a>
                )}
              </div>

            </div>
          </div>
        </div>
      )}
    </nav>
  )
}
